const DEFAULT_CALL_START_RETRY_TIMEOUT_MS = 30 * 1000;
const DEFAULT_REQUEST_TIMEOUT_MS = 5 * 1000;
const MAX_RETRY_DELAY_MS = 5 * 1000;

export function resolveCallStartRetryTimeoutMs(configStore) {
	const seconds = Number(configStore && configStore.webrtc &&
		configStore.webrtc.callStartRetryTimeoutSeconds);
	return Number.isFinite(seconds) && seconds > 0
		? Math.floor(seconds * 1000)
		: DEFAULT_CALL_START_RETRY_TIMEOUT_MS;
}

export function generateClientCallId(userId) {
	if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
		return window.crypto.randomUUID();
	}
	return `call-${userId || 0}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function isCallStartRetriableError(error) {
	const status = error && (error.statusCode || (error.response && error.response.status));
	if (status) {
		return status === 408 || status === 429 || status >= 500;
	}
	return !(error && typeof error.code === 'number');
}

export function createCallStartRetry(request, options = {}) {
	const timeoutMs = Number(options.timeoutMs) > 0
		? Number(options.timeoutMs)
		: DEFAULT_CALL_START_RETRY_TIMEOUT_MS;
	const deadline = Date.now() + timeoutMs;
	let cancelled = false;
	let retryTimer = null;
	let progressTimer = null;
	let retryCount = 0;
	let lastError = null;
	let abortReject = null;

	const clearTimers = () => {
		if (retryTimer) {
			clearTimeout(retryTimer);
			retryTimer = null;
		}
		if (progressTimer) {
			clearInterval(progressTimer);
			progressTimer = null;
		}
	};

	const emitProgress = () => {
		if (typeof options.onRetry !== 'function') {
			return;
		}
		options.onRetry({
			retryCount,
			remainingSeconds: Math.max(0, Math.ceil((deadline - Date.now()) / 1000)),
			error: lastError
		});
	};

	const startProgress = () => {
		emitProgress();
		if (!progressTimer) {
			progressTimer = setInterval(emitProgress, 250);
		}
	};

	const promise = new Promise((resolve, reject) => {
		abortReject = reject;
		const attempt = () => {
			if (cancelled) {
				clearTimers();
				return reject(Object.assign(new Error('通话已取消'), { cancelled: true }));
			}
			const remainingMs = deadline - Date.now();
			if (remainingMs <= 0) {
				clearTimers();
				return reject(Object.assign(lastError || new Error('连接服务超时'), { callStartTimeout: true }));
			}
			Promise.resolve(request({
				attempt: retryCount + 1,
				remainingMs,
				requestTimeoutMs: Math.max(500, Math.min(DEFAULT_REQUEST_TIMEOUT_MS, remainingMs))
			})).then((result) => {
				clearTimers();
				resolve(result);
			}).catch((error) => {
				lastError = error;
				if (cancelled) {
					clearTimers();
					return reject(Object.assign(new Error('通话已取消'), { cancelled: true }));
				}
				if (!isCallStartRetriableError(error)) {
					clearTimers();
					return reject(error);
				}
				retryCount++;
				startProgress();
				const nextRemainingMs = deadline - Date.now();
				if (nextRemainingMs <= 0) {
					clearTimers();
					return reject(Object.assign(error || new Error('连接服务超时'), { callStartTimeout: true }));
				}
				const delay = Math.min(1000 * Math.pow(2, Math.max(retryCount - 1, 0)), MAX_RETRY_DELAY_MS);
				retryTimer = setTimeout(attempt, Math.min(delay, nextRemainingMs));
			});
		};
		attempt();
	});

	return {
		promise,
		cancel() {
			if (cancelled) {
				return;
			}
			cancelled = true;
			clearTimers();
			if (abortReject) {
				abortReject(Object.assign(new Error('通话已取消'), { cancelled: true }));
			}
		},
		deadline
	};
}
